/**
 * GoalTracker — the acquisition instrument board on the admin Signup Funnel tab.
 *
 * Trials, activation and trial-to-paid live in one panel on purpose: a trial
 * count climbing toward target reads as progress unless the activation ratio is
 * right beside it, and August 2026 (6.3% activation, 16 July trials converting
 * to 0 paid) is exactly the case that has to stay visible. Do not split these
 * into separate cards or render the trial gauge alone.
 */
import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { eachDayOfInterval, format, isWithinInterval, parseISO } from 'date-fns'
import { Card } from '@/components/ui/card'
import { ChartContainer, type ChartConfig } from '@/components/ui/chart'
import { GoalGauge, TIER_DOT, TIER_TEXT } from './GoalGauge'
import {
  ACTIVATION_GATE,
  TRIAL_TARGET_PER_DAY,
  TRIAL_TO_PAID_TARGET,
  goalTier,
  prorateTrialTarget,
  type GoalTier,
} from '@/lib/constants/adminGoals'
import type { SignupFunnelResponse, SignupJourney } from '@/lib/api/admin-types'
import type { AdminFunnelWindow } from '@/lib/utils/adminPeriod'
import { cn } from '@/lib/utils'

type GoalTrackerProps = {
  data: SignupFunnelResponse
  /** Journeys after the workspace-joiner filter, so paid math matches the table. */
  journeys: SignupJourney[]
  range: AdminFunnelWindow
}

const TRIAL_EVENT = 'stripe_trial_started'

const TRIALS_CHART_CONFIG: ChartConfig = {
  count: { label: 'Trials', color: 'var(--chart-1)' },
}

function stageCount(data: SignupFunnelResponse, eventType: string): number {
  const stage = data.funnel?.core_stages?.find((s) => s.event_type === eventType)
  return stage?.count ?? 0
}

function fmtPct(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—'
  const v = ratio * 100
  return `${v >= 10 ? Math.round(v) : v.toFixed(1)}%`
}

/** Health dot + label for the strip across the top of the panel. */
function StatusPip({ label, tier }: { label: string; tier: GoalTier | null }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={cn(
          'size-1.5 rounded-full shrink-0',
          tier == null ? 'bg-muted-foreground/40' : TIER_DOT[tier]
        )}
        aria-hidden
      />
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/80">
        {label}
      </span>
    </span>
  )
}

function Instrument({
  label,
  children,
  footnote,
}: {
  label: string
  children: React.ReactNode
  footnote: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-1 px-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </span>
      {children}
      <p className="text-center text-[11px] text-muted-foreground">{footnote}</p>
    </div>
  )
}

export function GoalTracker({ data, journeys, range }: GoalTrackerProps) {
  const trials = stageCount(data, TRIAL_EVENT)
  const signupsCompleted = stageCount(data, 'signup_completed')
  const testsLaunched = stageCount(data, 'experiment_published')

  const trialTarget = prorateTrialTarget(range.days)
  const trialsPerDay = range.days > 0 ? trials / range.days : 0

  const activation = signupsCompleted > 0 ? testsLaunched / signupsCompleted : null

  /**
   * Trial-to-paid has to read one population. Both sides are counted from the
   * journeys in view, so the workspace-joiner filter cannot put the numerator and
   * denominator on different cohorts — and only trial starters are counted, since
   * a paid account that never started a trial in this window did not convert in it.
   * This is why the denominator can trail the headline trial count, which is the
   * unfiltered server-side rollup.
   */
  const { trialStarters, paidCount } = useMemo(() => {
    let trialStarters = 0
    let paidCount = 0
    for (const journey of journeys) {
      const startedTrial = (journey.sessions ?? []).some((session) =>
        (session.events ?? []).some((event) => event.event_type === TRIAL_EVENT)
      )
      if (!startedTrial) continue
      trialStarters += 1
      if (journey.subscription?.is_paid) paidCount += 1
    }
    return { trialStarters, paidCount }
  }, [journeys])

  const trialToPaid = trialStarters > 0 ? paidCount / trialStarters : null

  // Each gauge reads achieved-over-goal, so 1 means the target is met.
  const trialRatio = trialTarget > 0 ? trials / trialTarget : null
  const activationRatio = activation != null ? activation / ACTIVATION_GATE : null
  const conversionRatio = trialToPaid != null ? trialToPaid / TRIAL_TO_PAID_TARGET : null

  /**
   * Per-day trial bars — the thrust profile behind the headline number. Each axis
   * label is formatted from the same local Date that produced its bucket key, so
   * nothing round-trips through a date-only string and no label can drift a day.
   */
  const trialsByDay = useMemo(() => {
    const buckets = new Map<string, { label: string; count: number }>()
    for (const day of eachDayOfInterval({ start: range.from, end: range.to })) {
      buckets.set(format(day, 'yyyy-MM-dd'), { label: format(day, 'MMM d'), count: 0 })
    }
    for (const journey of journeys) {
      for (const session of journey.sessions ?? []) {
        for (const event of session.events ?? []) {
          if (event.event_type !== TRIAL_EVENT || !event.time) continue
          const at = parseISO(event.time)
          if (Number.isNaN(at.getTime())) continue
          if (!isWithinInterval(at, { start: range.from, end: range.to })) continue
          const key = format(at, 'yyyy-MM-dd')
          const bucket = buckets.get(key)
          if (bucket) bucket.count += 1
          else buckets.set(key, { label: format(at, 'MMM d'), count: 1 })
        }
      }
    }
    return Array.from(buckets, ([day, { label, count }]) => ({ day, label, count }))
  }, [journeys, range.from, range.to])

  const hasDailyTrials = trialsByDay.some((d) => d.count > 0)
  const trialTier = goalTier(trialRatio)
  const activationTier = goalTier(activationRatio)
  const conversionTier = goalTier(conversionRatio)

  return (
    // gap-0/py-0 so the status strip and sparkline dividers run edge to edge.
    <Card className="mb-4 gap-0 py-0">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border/40 px-6 py-3">
        <StatusPip label="Trial pace" tier={trialTier} />
        <StatusPip label="Activation" tier={activationTier} />
        <StatusPip label="Conversion" tier={conversionTier} />
        <span className="ml-auto text-[11px] text-muted-foreground tabular-nums">
          {format(range.from, 'MMM d, yyyy')} — {format(range.to, 'MMM d, yyyy')} ·{' '}
          {range.days} day{range.days === 1 ? '' : 's'}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-3">
        <Instrument
          label="Trials vs target"
          footnote={
            <>
              <span
                className={cn(
                  'tabular-nums',
                  trialTier ? TIER_TEXT[trialTier] : 'text-muted-foreground'
                )}
              >
                {trialsPerDay.toFixed(1)}/day
              </span>{' '}
              against a {TRIAL_TARGET_PER_DAY}/day pace
              {trials < trialTarget && (
                <> · {(trialTarget - trials).toLocaleString()} behind target</>
              )}
            </>
          }
        >
          <GoalGauge
            ratio={trialRatio}
            display={trials.toLocaleString()}
            caption={`of ${trialTarget.toLocaleString()}`}
          />
        </Instrument>

        <Instrument
          label="Activation"
          footnote={
            <>
              <span className="tabular-nums text-foreground">
                {testsLaunched.toLocaleString()}
              </span>{' '}
              of{' '}
              <span className="tabular-nums text-foreground">
                {signupsCompleted.toLocaleString()}
              </span>{' '}
              signups launched a test · gate {fmtPct(ACTIVATION_GATE)}
            </>
          }
        >
          <GoalGauge
            ratio={activationRatio}
            display={fmtPct(activation)}
            caption={`gate ${fmtPct(ACTIVATION_GATE)}`}
          />
        </Instrument>

        <Instrument
          label="Trial to paid"
          footnote={
            <>
              <span className="tabular-nums text-foreground">{paidCount.toLocaleString()}</span>{' '}
              paid of{' '}
              <span className="tabular-nums text-foreground">
                {trialStarters.toLocaleString()}
              </span>{' '}
              trials in view · target {fmtPct(TRIAL_TO_PAID_TARGET)}
            </>
          }
        >
          <GoalGauge
            ratio={conversionRatio}
            display={fmtPct(trialToPaid)}
            caption={`target ${fmtPct(TRIAL_TO_PAID_TARGET)}`}
          />
        </Instrument>
      </div>

      <div className="border-t border-border/40 px-6 py-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Trials per day
          </span>
          <span className="text-[10px] text-muted-foreground">
            dashed line = {TRIAL_TARGET_PER_DAY}/day pace
          </span>
        </div>
        {hasDailyTrials ? (
          <ChartContainer config={TRIALS_CHART_CONFIG} className="h-[132px] w-full">
            <BarChart data={trialsByDay} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 9, fill: 'var(--muted-foreground)' }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis hide domain={[0, 'dataMax']} />
              <Tooltip
                cursor={{ fill: 'var(--muted)', opacity: 0.3 }}
                contentStyle={{
                  backgroundColor: 'var(--popover)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  fontSize: 11,
                }}
                formatter={(value: number) => [`${value} trial${value === 1 ? '' : 's'}`, '']}
              />
              <ReferenceLine
                y={TRIAL_TARGET_PER_DAY}
                stroke="var(--foreground)"
                strokeDasharray="4 4"
                strokeOpacity={0.4}
              />
              <Bar dataKey="count" fill="var(--chart-1)" radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ChartContainer>
        ) : (
          <p className="py-4 text-center text-[11px] text-muted-foreground">
            No trial events in this window.
          </p>
        )}
      </div>
    </Card>
  )
}
